import { TrendingUp, Award, FileText, BarChart3, CheckCircle2, AlertCircle, Target } from 'lucide-react';

export default function EvaluationReport() {
  const skills = [
    { name: "React & Frontend", score: 92, status: "excellent" },
    { name: "Problem Solving", score: 88, status: "excellent" },
    { name: "Communication", score: 85, status: "good" },
    { name: "Team Collaboration", score: 90, status: "excellent" },
    { name: "Code Quality", score: 87, status: "good" },
  ];

  const insights = [
    {
      icon: CheckCircle2,
      type: "Strength",
      text: "Strong technical foundation with React and modern frameworks",
      color: "emerald"
    },
    {
      icon: CheckCircle2,
      type: "Strength",
      text: "Excellent problem-solving approach with systematic thinking",
      color: "emerald"
    },
    {
      icon: AlertCircle,
      type: "Area to Explore",
      text: "Could elaborate more on scalability considerations",
      color: "amber"
    },
  ];

  return (
    <div className="py-32 bg-transparent relative overflow-hidden">

      <div className="absolute top-40 left-1/4 w-96 h-96 bg-emerald-500 rounded-full filter blur-3xl opacity-10"></div>
      <div className="absolute bottom-40 right-1/4 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl opacity-10"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 glass-dark rounded-full mb-6">
            <FileText className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-medium text-white">Detailed Analysis</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold mb-6 text-white">
            Comprehensive
            <span className="block mt-2 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
              Evaluation Reports
            </span>
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            AI-generated insights with detailed scoring, skill analysis, and actionable recommendations
          </p>
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="glass-dark rounded-3xl p-8 border border-emerald-500/20">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Sarah Johnson</h3>
                    <p className="text-slate-400">Senior Frontend Developer Position</p>
                  </div>
                  <div className="text-center">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-2">
                      <span className="text-3xl font-bold text-white">A+</span>
                    </div>
                    <p className="text-sm text-slate-400">Overall Grade</p>
                  </div>
                </div>

                <div className="space-y-4 mb-8">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-emerald-400" />
                    Skill Assessment
                  </h4>
                  {skills.map((skill, i) => (
                    <div key={i} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-300 font-medium">{skill.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-semibold">{skill.score}%</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            skill.status === 'excellent'
                              ? 'bg-emerald-500/20 text-emerald-400'
                              : 'bg-teal-500/20 text-teal-400'
                          }`}>
                            {skill.status}
                          </span>
                        </div>
                      </div>
                      <div className="h-2.5 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-1000"
                          style={{ width: `${skill.score}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pt-6 border-t border-slate-700">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-teal-400" />
                    Key Insights
                  </h4>
                  <div className="space-y-3">
                    {insights.map((insight, i) => (
                      <div key={i} className={`flex items-start gap-3 p-4 rounded-xl ${
                        insight.color === 'emerald'
                          ? 'bg-emerald-500/10 border border-emerald-500/30'
                          : 'bg-amber-500/10 border border-amber-500/30'
                      }`}>
                        <insight.icon className={`w-5 h-5 flex-shrink-0 ${
                          insight.color === 'emerald' ? 'text-emerald-400' : 'text-amber-400'
                        }`} />
                        <div>
                          <p className={`text-xs font-semibold mb-1 ${
                            insight.color === 'emerald' ? 'text-emerald-400' : 'text-amber-400'
                          }`}>
                            {insight.type}
                          </p>
                          <p className="text-slate-300 text-sm">{insight.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="glass-dark rounded-3xl p-8 border border-teal-500/20">
                <h4 className="text-lg font-semibold text-white mb-4">Interview Summary</h4>
                <div className="prose prose-invert max-w-none">
                  <p className="text-slate-300 leading-relaxed mb-4">
                    Sarah demonstrated exceptional technical knowledge and communication skills throughout the interview.
                    Her responses showed deep understanding of React ecosystem, state management, and performance optimization.
                  </p>
                  <p className="text-slate-300 leading-relaxed">
                    She articulated complex concepts clearly and showed strong problem-solving abilities when presented
                    with scenario-based questions. Her collaborative mindset and code quality awareness make her a strong
                    candidate for senior positions.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="glass-dark rounded-3xl p-6 border border-emerald-500/20">
                <div className="flex items-center gap-3 mb-6">
                  <Award className="w-6 h-6 text-emerald-400" />
                  <h3 className="text-lg font-semibold text-white">Quick Stats</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl">
                    <span className="text-slate-400">Interview Duration</span>
                    <span className="text-white font-semibold">28 min</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl">
                    <span className="text-slate-400">Questions Asked</span>
                    <span className="text-white font-semibold">15</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl">
                    <span className="text-slate-400">Response Quality</span>
                    <span className="text-emerald-400 font-semibold">High</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl">
                    <span className="text-slate-400">Confidence Level</span>
                    <span className="text-emerald-400 font-semibold">92%</span>
                  </div>
                </div>
              </div>

              <div className="glass-dark rounded-3xl p-6 border border-teal-500/20">
                <div className="flex items-center gap-3 mb-6">
                  <TrendingUp className="w-6 h-6 text-teal-400" />
                  <h3 className="text-lg font-semibold text-white">Recommendation</h3>
                </div>
                <div className="p-6 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-xl border-2 border-emerald-500/50">
                  <p className="text-2xl font-bold text-white mb-2">Highly Recommended</p>
                  <p className="text-slate-300 text-sm">
                    Strong candidate for immediate consideration. Schedule technical round with team lead.
                  </p>
                </div>
              </div>

              <div className="glass-dark rounded-3xl p-6 border border-cyan-500/20">
                <h4 className="text-sm font-semibold text-slate-400 mb-4">MATCH SCORE</h4>
                <div className="relative w-full aspect-square">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 200 200">
                    <circle
                      cx="100"
                      cy="100"
                      r="80"
                      fill="none"
                      stroke="rgb(51, 65, 85)"
                      strokeWidth="24"
                    />
                    <circle
                      cx="100"
                      cy="100"
                      r="80"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="24"
                      strokeDasharray="502.4"
                      strokeDashoffset="50.24"
                      strokeLinecap="butt"
                    />
                    <circle
                      cx="100"
                      cy="100"
                      r="80"
                      fill="none"
                      stroke="#14b8a6"
                      strokeWidth="24"
                      strokeDasharray="502.4"
                      strokeDashoffset="-125.6"
                      strokeLinecap="butt"
                    />
                    <circle
                      cx="100"
                      cy="100"
                      r="80"
                      fill="none"
                      stroke="#06b6d4"
                      strokeWidth="24"
                      strokeDasharray="502.4"
                      strokeDashoffset="-226.08"
                      strokeLinecap="butt"
                    />
                    <circle
                      cx="100"
                      cy="100"
                      r="80"
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="24"
                      strokeDasharray="502.4"
                      strokeDashoffset="-326.56"
                      strokeLinecap="butt"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-white">90%</div>
                      <div className="text-sm text-slate-400">Match</div>
                    </div>
                  </div>
                </div>
                <div className="space-y-3 pt-6 mt-6 border-t border-slate-700">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                      <span className="text-slate-300">Technical Skills</span>
                    </div>
                    <span className="text-white font-semibold">35%</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                      <span className="text-slate-300">Experience Match</span>
                    </div>
                    <span className="text-white font-semibold">25%</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                      <span className="text-slate-300">Culture Fit</span>
                    </div>
                    <span className="text-white font-semibold">20%</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span className="text-slate-300">Communication</span>
                    </div>
                    <span className="text-white font-semibold">10%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 text-center">
          <div className="glass-white inline-block px-8 py-6 rounded-3xl">
            <p className="text-slate-900 font-semibold mb-4">
              Every report includes detailed transcripts, skill breakdowns, and AI-generated insights
            </p>
            <a href="https://sandeshlekhwani.github.io/GreentickCandidateReports/sandeshgtreport.html" target="_blank" rel="noopener noreferrer" className="inline-block px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-semibold hover:shadow-2xl hover:shadow-emerald-500/50 transition-all hover:scale-105">
              See Sample Report
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
